import React from 'react';
import { Button, makeStyles } from '@material-ui/core';
import { darken } from '@material-ui/core/styles';
import { WhatsApp } from '@material-ui/icons';
import AdVariantGrid, { GridCell } from './AdVariantGrid';
import { AD } from './adsAgentTokens';
import { GeneratedTemplate } from './adsGenerator.types';
import { generationLabel } from './generationStatus';

// The visitor's own ads: console skeletons while the Ads Agent job runs, then
// the console cards once variants are ready. Earlier generations follow in the
// same grid, like the console's ad templates list.

const IN_PROGRESS = ['pending', 'processing'];
const NOT_READY = ['pending', 'running', 'generating'];

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: AD.fontFamily,
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    margin: '0 0 16px',
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: AD.text,
  },
  sub: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    margin: '4px 0 0',
    fontSize: 13,
    color: AD.textMuted,
    lineHeight: 1.5,
  },
  waIcon: {
    fontSize: 16,
    color: '#109d59',
  },
  signup: {
    height: AD.controlHeight,
    padding: '0 16px',
    borderRadius: AD.radiusLg,
    background: AD.cta,
    color: AD.onPrimary,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 12.5,
    whiteSpace: 'nowrap',
    '&:hover': { background: darken(AD.cta, 0.15) },
  },
}));

const toCells = (template: GeneratedTemplate): GridCell[] => {
  const ready = template.variants.filter((v) => !NOT_READY.includes(v.variantStatus));
  const inFlight = IN_PROGRESS.includes(template.status);
  const total = inFlight ? Math.max(template.templateCount || 0, ready.length) : ready.length;

  return Array.from({ length: total }, (_, index) => {
    const variant = ready[index];
    if (!variant) {
      return { key: `${template._id}:skeleton:${index}`, kind: 'skeleton', progress: template.progress };
    }
    return { key: `${template._id}:${variant.variantIndex}`, kind: 'variant', variant };
  });
};

const GeneratedAdsSection = (props: Props): React.ReactElement => {
  const { template, earlierTemplates = [], waMessageSent, signupUrl } = props;
  const classes = useStyles();
  const inFlight = IN_PROGRESS.includes(template.status);

  return (
    <section className={classes.root} aria-label="Your ads">
      <div className={classes.head}>
        <div>
          <h2 className={classes.title}>
            {inFlight ? generationLabel(template.progress) : 'Your ads are ready'}
          </h2>
          <p className={classes.sub}>
            {inFlight ? (
              'This takes a few minutes. You can keep this page open — we will also send the ads to your WhatsApp.'
            ) : waMessageSent ? (
              <>
                <WhatsApp className={classes.waIcon} />
                We have also sent these ads to your WhatsApp.
              </>
            ) : (
              'Sign up to save these ads and run them on Facebook and Instagram.'
            )}
          </p>
        </div>
        {!inFlight ? (
          <Button
            className={classes.signup}
            component="a"
            href={signupUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Sign up to run these ads
          </Button>
        ) : null}
      </div>
      <AdVariantGrid cells={[template, ...earlierTemplates].flatMap(toCells)} />
    </section>
  );
};

interface Props {
  template: GeneratedTemplate;
  earlierTemplates?: GeneratedTemplate[];
  waMessageSent: boolean;
  signupUrl: string;
}

export default GeneratedAdsSection;
